/**
 * Shared utilities for useUISpec
 *
 * Contains the static fallback spec builder and SSE stream parser,
 * used by both the main hook and the streaming module.
 *
 * Extracted from use-ui-spec.ts to comply with <300 lines/file rule.
 *
 * Task: NocoDB #2675 — file split
 * Agent: Diego <diego@liftping.net>
 */

import type { Spec } from "@json-render/core";
import type { DramaAnalysisResponse } from "@dramadetector/shared";

// ============================================================================
// Timeout configuration (shared between main hook, streaming, and batch)
// ============================================================================

/** Abort edge function call after 5s (Priya spec: "times out within 5s on failure") */
export const EDGE_FN_TIMEOUT_MS = 5_000;

/** Streaming timeout — longer than batch since partial data arrives earlier */
export const STREAMING_TIMEOUT_MS = 15_000;

/**
 * First-chunk timeout for streaming mode.
 * If no SSE data arrives within this window, abort streaming and fall back
 * to batch mode. Catches dead streams (cold start hang, LLM stall) without
 * making users wait the full STREAMING_TIMEOUT_MS.
 */
export const STREAMING_FIRST_CHUNK_TIMEOUT_MS = 5_000;

export type UISpecTier = "anonymous" | "free" | "drama_plus" | "dramax";

// ============================================================================
// SSE event types (matches server-side events from generate-ui-spec)
// ============================================================================

interface SSEPartialEvent {
  type: "partial";
  spec: Partial<Spec>;
}

interface SSEDoneEvent {
  type: "done";
  spec: Spec;
}

interface SSEErrorEvent {
  type: "error";
  error: string;
}

type SSEEvent = SSEPartialEvent | SSEDoneEvent | SSEErrorEvent;

// ============================================================================
// Static fallback spec
// ============================================================================

/**
 * Minimal static spec returned when the edge function errors or times out.
 * Shows the drama score and summary — always-available data from the
 * DramaAnalysisResponse passed by the caller.
 */
export function buildFallbackSpec(
  analysisResult: DramaAnalysisResponse,
  userTier: UISpecTier
): Spec {
  const score = analysisResult.result?.score ?? analysisResult.score ?? 0;
  const category =
    analysisResult.result?.category ??
    analysisResult.category ??
    "safe";
  const summary =
    analysisResult.result?.summary ?? analysisResult.summary ?? "";

  const elements: Spec["elements"] = {
    "root-card": {
      type: "AnalysisCard",
      props: {
        title: "Analysis Results",
        subtitle: "",
        variant: "standard",
      },
    },
    "drama-score": {
      type: "DramaScore",
      props: {
        score,
        category,
        size: category === "high_drama" || category === "extreme_drama" ? "lg" : "md",
        showLabel: true,
      },
    },
    "drama-badge": {
      type: "DramaCategoryBadge",
      props: {
        category,
        size: "md",
        showIcon: true,
      },
    },
    "divider-1": {
      type: "SectionDivider",
      props: { variant: "subtle", spacing: "md" },
    },
    "summary-block": {
      type: "NarrativeBlock",
      props: {
        text: summary,
        emphasis: "normal",
      },
    },
  };

  const children: string[] = [
    "drama-score",
    "drama-badge",
    "divider-1",
    "summary-block",
  ];

  // Share action — available to all tiers
  elements["action-share"] = {
    type: "ActionButton",
    props: {
      label: "Share Analysis",
      action: "share_analysis",
      tier: "free",
      variant: "secondary",
      disabled: false,
    },
  };
  children.push("action-share");

  // Start transformation — DramaX only
  if (userTier === "dramax") {
    elements["action-transform"] = {
      type: "ActionButton",
      props: {
        label: "Start Transformation",
        action: "start_transformation",
        tier: "dramax",
        variant: "primary",
        disabled: false,
      },
    };
    children.push("action-transform");
  }

  // Upgrade CTA — free tier
  if (userTier === "free") {
    elements["action-upgrade"] = {
      type: "ActionButton",
      props: {
        label: "Upgrade to Drama+",
        action: "navigate_to_upgrade",
        tier: "free",
        variant: "primary",
        disabled: false,
      },
    };
    children.push("action-upgrade");
  }

  // Wire children into root card
  (elements["root-card"] as any).children = children;

  return {
    root: "root-card",
    elements,
    state: {
      tier: userTier,
    },
  };
}

// ============================================================================
// SSE Stream Parser
// ============================================================================

/**
 * Parse SSE events from a ReadableStream.
 *
 * Handles the text/event-stream format:
 *   data: {"type":"partial","spec":{...}}\n\n
 *   data: {"type":"done","spec":{...}}\n\n
 *
 * @param reader - ReadableStreamDefaultReader from fetch response
 * @param onPartial - Called for each partial spec update
 * @param onDone - Called with the final tier-guarded spec
 * @param onError - Called on stream error
 */
export async function parseSSEStream(
  reader: ReadableStreamDefaultReader<Uint8Array>,
  onPartial: (spec: Partial<Spec>) => void,
  onDone: (spec: Spec) => void,
  onError: (error: string) => void,
  onRawData?: () => void,
): Promise<void> {
  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    // Signal that raw bytes arrived (used by first-chunk timeout)
    onRawData?.();

    buffer += decoder.decode(value, { stream: true });

    // Process complete SSE events (separated by double newline)
    const events = buffer.split("\n\n");
    // Keep the last partial event in the buffer
    buffer = events.pop() ?? "";

    for (const eventStr of events) {
      const trimmed = eventStr.trim();
      if (!trimmed) continue;

      // SSE format: "data: {...json...}"
      const dataPrefix = "data: ";
      if (!trimmed.startsWith(dataPrefix)) continue;

      const jsonStr = trimmed.slice(dataPrefix.length);
      try {
        const event = JSON.parse(jsonStr) as SSEEvent;

        switch (event.type) {
          case "partial":
            onPartial(event.spec);
            break;
          case "done":
            onDone(event.spec);
            return; // Stream complete
          case "error":
            onError(event.error);
            return;
        }
      } catch (_parseErr) {
        // Skip malformed events — best effort progressive rendering
      }
    }
  }
}
